import { computed, reactive, ref } from 'vue'
import { camelizeKeys } from 'humps'
import { useLoader } from '../useLoader'
import { useError } from '../useError'
import { useYearSelector } from '@/services/useYearSelector'
import { useDialog } from '@/services/useDialog'
import { useBudgets } from '@/services/useBudgets'
import { expenseTypesToArray } from '@/services/useExpenseType'
import axios from '@/axios'

const receiptsState = reactive({
  receipts: [],
})

let currentReceipt = reactive({
  expenseTypeId: '',
  haveReceiptImages: false,
  creditorInfo: '',
  expenseContent1: '',
  expenseContent2: '',
  user: '',
  expenseAmount: null,
  denominator: null,
  numerator: null,
  watchEffectResult: 0,
  expenseDate: undefined,
  updateDate: undefined,
  isDistributed: false,
  partialAmount: null,
  partialComment: '',
  isPartial: false,
  files: [],
  additionalResources: [],
  sentBackItems: [],
})

const filteredStatus = ref([])
const isReversedSort = ref(false)

export const useReceipts = () => {
  const { toggleShowLoader } = useLoader()
  const { toggleShowError } = useError()
  /**
   * statsCode\
   *  1: 作成中\
   *  2: 承認待\
   *  3: 差戻し\
   *  4: 承認済
   */
  const resetCurrentReceipt = () => {
    currentReceipt = {
      expenseTypeId: '',
      haveReceiptImages: false,
      expenseContent1: '',
      expenseContent2: '',
      creditorInfo: '',
      user: '',
      expenseAmount: null,
      denominator: null,
      numerator: null,
      expenseDate: undefined,
      updateDate: undefined,
      isDistributed: false,
      partialAmount: null,
      partialComment: '',
      isPartial: false,
      files: [],
      additionalResources: [],
      sentBackItems: [],
      option: {
        viewType: 1,
      },
    }
  }

  const setCurrentReceipt = async receiptId => {
    const res = await getReceipt(receiptId)
    if (res) {
      currentReceipt = { ...res }
      if (currentReceipt.expenseTypeId != null) {
        currentReceipt.expenseTypeId = expenseTypesToArray(
          currentReceipt.expenseTypeId,
        )
      } else {
        currentReceipt.expenseTypeId = []
      }
      currentReceipt.files = []
      if (currentReceipt.partialAmount || currentReceipt.partialComment) {
        currentReceipt.isPartial = true
      }
      // 入力時のチェックボックスのboolとhaveReceiptImagesのboolの判定が逆のためhaveReceiptImagesのboolを反転させる
      currentReceipt.haveReceiptImages = !currentReceipt.haveReceiptImages
      return new Promise(resolve => resolve(true))
    }
  }

  /**
   * 諸経費リストを取得する
   *
   * @param {number} year 取得する年度
   */
  const getReceipts = async (year = new Date().getFullYear()) => {
    try {
      toggleShowLoader(true)
      let res = await axios.get(`/receipts?year=${year}`)
      res = camelizeKeys(res)
      receiptsState.receipts = Array.from(res.data.receipts)
      toggleShowLoader(false)
    } catch (e) {
      toggleShowError(true)
      toggleShowLoader(false)
    }
  }

  /**
   * 諸経費を取得、キャメルケースに変換した諸経費オブジェクトを返す
   *
   * @param {number} id receipts.id
   * @returns object
   */
  const getReceipt = async id => {
    try {
      toggleShowLoader(true)
      const res = await axios.get(`/receipts/${id}`)
      const receipt = res?.data?.receipt ?? null
      if (receipt === null) {
        throw res
      }
      const camelizedReceipt = camelizeKeys(receipt)
      toggleShowLoader(false)
      return camelizedReceipt
    } catch (e) {
      toggleShowError(true)
      toggleShowLoader(false)
    }
  }

  const downloadReceipt = receiptId => {
    axios
      .get(`/download/receipts/${receiptId}`, {
        responseType: 'blob',
      })
      .then(response => {
        const content_type = response.headers['content-type']
        const blob = new Blob([response.data], { type: content_type })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')

        //ファイル名をレスポンスヘッダから取得したい
        let filename = '領収書.pdf'
        const disposition = response.headers['content-disposition'].toString()
        if (disposition != null) {
          let findex = disposition.indexOf('filename*=')
          if (findex != -1) {
            filename = disposition.substring(findex + 10)
            try {
              filename = decodeURI(
                filename.substring(filename.indexOf("''") + 2),
              )
            } catch (e) {
              filename = '領収書_.pdf'
            }
          } else {
            findex = disposition.indexOf('filename=')
            if (findex != -1) {
              filename = disposition.substring(findex + 9)
            }
          }
        }

        link.download = filename
        link.href = url
        link.click()
      })
  }

  /**
   * レスポンスが正常な場合諸経費リストを取得する
   *
   * @param {object} response
   * @returns void
   */
  const getReceiptsAfterResponse = async response => {
    if (response?.data?.code !== 0) {
      return
    }
    const { closeDialog } = useDialog()
    const { currentYear } = useYearSelector()
    const { getRemainingBudget } = useBudgets()
    await getRemainingBudget()
    await getReceipts(currentYear.value)
    closeDialog()
  }

  /**
   * 諸経費を削除する
   *
   * @param {int} receiptId receipts.id
   */
  const deleteReceipt = async receiptId => {
    const res = await axios.delete(`/receipts/${receiptId}`)
    getReceiptsAfterResponse(res)
  }

  return {
    receipts: computed(() => receiptsState.receipts),
    getReceipts,
    deleteReceipt,
    setCurrentReceipt,
    resetCurrentReceipt,
    getReceiptsAfterResponse,
    downloadReceipt,
    currentReceipt,
    filteredStatus,
    isReversedSort,
  }
}
